import { Link } from "react-router-dom";
import { useApp } from "@/store/AppStore";
import { useAsesor } from "@/hooks/useAsesor";
import SectionTitle from "@/components/base/SectionTitle";
import AsesorStats from "@/pages/asesor/components/AsesorStats";
import InstallHint from "@/pages/asesor/components/InstallHint";
import { saleMatchesPeriod } from "@/utils/kpi";
import { monthLabel } from "@/utils/format";

const SCREENS = [
  {
    icon: "ri-store-2-line",
    title: "Catálogo Amway",
    desc: "Explora todo el portafolio con precio, tu costo y tu ganancia estimada. Solo consulta.",
    path: "/asesor/catalogo",
  },
  {
    icon: "ri-archive-2-line",
    title: "Mi Inventario",
    desc: "Tus productos en consignación con precio costo asesor y ganancia estimada.",
    path: "/asesor/inventario",
  },
  {
    icon: "ri-checkbox-circle-line",
    title: "Registrar Venta",
    desc: "Registra tu venta a un cliente (existente o nuevo) y descuenta tu stock al instante.",
    path: "/asesor/chulear",
  },
  {
    icon: "ri-id-card-line",
    title: "Mi Cuenta",
    desc: "Tu ficha: ventas realizadas, tenencia, deuda y tu margen de ganancia.",
    path: "/asesor/cuenta",
  },
  {
    icon: "ri-shopping-cart-2-line",
    title: "Pedido por Encargo",
    desc: "Solicita productos fuera de stock desde el catálogo completo de Amway.",
    path: "/asesor/encargo",
  },
  {
    icon: "ri-image-2-line",
    title: "Kit de Ventas",
    desc: "Imágenes y copias listas para compartir en WhatsApp y redes sociales.",
    path: "/asesor/kit",
  },
];

const AsesorHome = () => {
  const { data } = useApp();
  const { name, units, inventoryValue, sales, margin } = useAsesor();

  const periodSales = sales.filter((s) => saleMatchesPeriod(s, data.period));
  const salesTotal = periodSales.reduce((acc, s) => acc + s.total, 0);

  return (
    <div className="pb-24">
      <div className="relative overflow-hidden rounded-lg glass-panel border-accent-500/25 mb-6">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-accent-500/10 blur-2xl" />
        <div className="relative px-5 md:px-7 py-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-500/20 text-accent-200">
              <i className="ri-user-star-line text-sm" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-300">
              Portal del Asesor · Tu app de ventas
            </span>
          </div>
          <h1 className="editable-title text-2xl md:text-3xl font-extrabold tracking-tight">
            Hola, {name}
          </h1>
          <p className="text-xs md:text-sm text-foreground-600 mt-2 max-w-2xl">
            Este es tu panel personal. Aquí ves tu mercancía en consignación, registras tus ventas y
            pides productos por encargo. Tu margen comercial es del {margin}%.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-accent-500/15 text-accent-200">
              Margen {margin}%
            </span>
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-background-200/60 text-foreground-700">
              {periodSales.length} ventas en {monthLabel(data.period.month)}
            </span>
          </div>
        </div>
      </div>

      <InstallHint />

      <AsesorStats
        units={units}
        inventoryValue={inventoryValue}
        salesCount={periodSales.length}
        salesTotal={salesTotal}
      />

      <div className="mt-6">
        <SectionTitle
          text="Tus pantallas"
          subtitle="Accesos directos a todo lo que necesitas para vender."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SCREENS.map((s) => (
            <Link
              key={s.path}
              to={s.path}
              className="glass-panel rounded-lg p-5 animate-fade-up group cursor-pointer hover:border-accent-500/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="w-11 h-11 rounded-lg bg-primary-500/25 text-accent-200 flex items-center justify-center">
                  <i className={`${s.icon} text-xl`} />
                </span>
                <i className="ri-arrow-right-line text-foreground-500 group-hover:text-accent-200 transition-colors" />
              </div>
              <h3 className="editable-title text-base font-bold mt-4">{s.title}</h3>
              <p className="text-xs md:text-sm text-foreground-600 mt-2">{s.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AsesorHome;